/**
 * Konflikt-Verwaltung: Live-Hook + drei Auflösungs-Strategien.
 *
 *   1. resolveKeepLocal:    Lokale Änderung gewinnt → Server-Stand als baseLastModified
 *                           setzen, Outbox-Eintrag freischalten, beim nächsten Sync
 *                           wird die Mutation erneut gesendet (diesmal ohne Konflikt).
 *
 *   2. resolveKeepServer:   Server-Version gewinnt → lokale Schicht durch
 *                           Server-Version ersetzen, Outbox-Eintrag verwerfen.
 *
 *   3. resolveKeepBoth:     Beide Versionen behalten — Server-Version übernehmen
 *                           und zusätzlich eine neue Schicht (mit den lokalen
 *                           Werten) anlegen.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { db, type LocalShift, type OutboxEntry } from '../db/schema';
import * as outbox from '../db/outbox';
import type { CreateShiftInput } from '../lib/shifts';

/** Live-Hook: alle Outbox-Einträge mit aktivem Konflikt. */
export function useConflicts(): OutboxEntry[] | undefined {
  return useLiveQuery(() => outbox.conflicts(), []);
}

/** Live-Anzahl der Konflikte (für den Status-Indikator im Header). */
export function useConflictCount(): number {
  return useLiveQuery(() => outbox.conflictCount(), [], 0) ?? 0;
}

// ---------------------------------------------------------------------------

/**
 * Lokale Version durchsetzen — der nächste Sync schickt das Update mit dem
 * neuen `baseLastModified` aus der Server-Version, also ohne Konflikt.
 *
 * Hinweis: bei `shift.delete`-Konflikten ergibt das Sinn, wenn der User die
 * Schicht trotzdem löschen will. Der Server bekommt einen DELETE mit dem
 * aktuellen Server-Stand als Base — das geht durch.
 */
export async function resolveKeepLocal(entry: OutboxEntry): Promise<void> {
  if (!entry.conflict) return;
  await outbox.clearConflict(entry.id!, {
    baseLastModified: entry.conflict.serverVersion.lastModified,
    // Payload bleibt unverändert.
  });
}

/**
 * Server-Version übernehmen — lokale Schicht ersetzen, Outbox-Eintrag löschen.
 */
export async function resolveKeepServer(entry: OutboxEntry): Promise<void> {
  if (!entry.conflict) return;
  const server = entry.conflict.serverVersion;
  // Lokal überschreiben, pending entfernen.
  await db.shifts.put({ ...server, pending: false });
  await outbox.remove(entry.id!);
}

/**
 * Beide Versionen behalten:
 *  - Server-Version lokal übernehmen.
 *  - Lokale Version als neue Schicht (Create) in die Outbox schieben — sie
 *    bekommt eine neue temporäre ID, wird beim nächsten Sync angelegt.
 */
export async function resolveKeepBoth(entry: OutboxEntry): Promise<void> {
  if (!entry.conflict) return;
  const { serverVersion, localVersion } = entry.conflict;

  // 1. Lokal: Server-Version setzen.
  await db.shifts.put({ ...serverVersion, pending: false });

  // 2. Lokale Werte als neue Schicht ablegen (für Create-Outbox).
  const newId =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = Math.floor(Math.random() * 16);
          return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });
  const local: LocalShift = {
    ...localVersion,
    id: newId,
    clientUuid: newId,
    createdAt: new Date().toISOString(),
    lastModified: new Date().toISOString(),
    deletedAt: null,
    pending: true,
  };
  await db.shifts.put(local);
  // Gesamtprüfung 11.09.2026: vollständiger Datensatz (Person, offenes Ende,
  // Geräte-UUID) — vorher fehlten diese Felder, die Kopie landete ggf. bei der
  // falschen Person bzw. ohne „Ware“-Kennzeichen.
  const createPayload: CreateShiftInput = {
    userId: localVersion.userId,
    clientUuid: newId,
    isOpenEnd: localVersion.isOpenEnd,
    locationId: localVersion.locationId,
    templateId: localVersion.templateId,
    shiftDate: localVersion.shiftDate,
    startTime: localVersion.startTime,
    endTime: localVersion.endTime,
    status: localVersion.status,
    note: localVersion.note,
    hourlyWage: localVersion.hourlyWage ?? undefined,
    bonusHours: localVersion.bonusHours,
  };
  await outbox.enqueue('shift.create', newId, createPayload);

  // 3. Den Konflikt-Eintrag verwerfen — die Server-Version ist ja jetzt
  //    lokal angekommen, eine erneute Mutation darauf braucht es nicht.
  await outbox.remove(entry.id!);
}

// Hilfsdatentyp für UI: schon ausgewertete Diff-Felder.
export interface ConflictDiffField {
  label: string;
  local: string;
  server: string;
  changed: boolean;
}

const STATUS_LABEL: Record<string, string> = {
  planned: 'geplant',
  done: 'erledigt',
  cancelled: 'abgesagt',
};

/** Liefert eine Liste aller Felder mit lokalem vs. Server-Wert. */
export function diffShiftFields(entry: OutboxEntry): ConflictDiffField[] {
  if (!entry.conflict) return [];
  const l = entry.conflict.localVersion;
  const s = entry.conflict.serverVersion;
  const formatHours = (n: number | undefined) =>
    n && n > 0 ? `+${n.toFixed(2).replace('.', ',')} h` : '—';
  const fields: ConflictDiffField[] = [
    field('Datum', l.shiftDate, s.shiftDate),
    field('Start', l.startTime, s.startTime),
    field('Ende', l.endTime, s.endTime),
    field('Status', STATUS_LABEL[l.status] ?? l.status, STATUS_LABEL[s.status] ?? s.status),
    field('Zusatzstunden', formatHours(l.bonusHours), formatHours(s.bonusHours)),
    field('Notiz', l.note ?? '—', s.note ?? '—'),
  ];
  return fields;
}

function field(label: string, local: string, server: string): ConflictDiffField {
  return { label, local, server, changed: local !== server };
}
